import clsx from "clsx";
import { addDays } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Fade from "@material-ui/core/Fade";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ArrowBack from "@material-ui/icons/ArrowBack";
import ChevronRight from "@material-ui/icons/ChevronRight";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import CommentIcon from "components/Icons/Comment";
import FacilityIcon from "components/Icons/Facility";
import { useWindowSize } from "hooks/screen";
import { FacilitiesState } from "store/reducers/facilities";
import { isAfterDate, isBeforeDate } from "utils/date-utils";
import { getFacilityOptions } from "utils/facilities";
import CancelConfirmation from "./CancelConfirmation";
import LinkMenuItem from "./LinkMenuItem";
import ScheduleDateMenuFacility from "./ScheduleDateMenuFacility";

import useStyles from "./styles";

export interface Props {
  horse: racehorse360.IHorse;
  workoutData: racehorse360.IWorkoutRequest;
  className?: string;
  onChange: (workoutData: racehorse360.IWorkoutRequest) => void;
  onClose: () => void;
  anchorEl?: HTMLDivElement;
  isOpen: boolean;
  onOpen?: () => void;
}

const ScheduleDateMenu = (props: Props) => {
  const {
    horse,
    anchorEl,
    onClose,
    workoutData,
    onChange,
    onOpen,
    isOpen = false
  } = props;

  const classes = useStyles();
  const { width, height } = useWindowSize();
  const arrowRef = useRef<HTMLDivElement>();
  const [activeView, setActiveView] = useState("default");
  const [arrowLeft, setArrowLeft] = useState(null);
  const [menuEl, setMenuEl] = useState(null);
  const [isBottomMenu, setIsBottomMenu] = useState(true);
  const menuRect = menuEl?.getBoundingClientRect() || {};

  const facilities = useSelector(
    (state: { facilities: FacilitiesState }) => state?.facilities?.facilities
  );

  const [facilityOptions, setFacilityOptions] = useState(
    getFacilityOptions(facilities, false, false, true)
  );

  useEffect(() => {
    if (facilities?.length) {
      setFacilityOptions(getFacilityOptions(facilities, false, false, true));
    }
  }, [facilities]);

  const isBeforeTomorrow = date => isBeforeDate(date, addDays(new Date(), 1));
  const isAfterTomorrow = date => isAfterDate(date, addDays(new Date(), 1));

  const handleClose = event => {
    event.stopPropagation();
    onClose();
  };
  const handleExited = () => setActiveView("default");
  const handleBackClick = () => setActiveView("default");

  const handleChangeFacilityClick = event => {
    event.stopPropagation();
    // Next two days validation temporarily discarded as per https://xbglobal.atlassian.net/browse/RAC-2657
    // if (workoutData && !isBeforeTomorrow(parseISO(workoutData.date))) {
    if (workoutData) {
      setActiveView("facility");
    }
  };

  const handleFacilityClick = id => {
    const edited = {
      ...workoutData,
      facility: facilities.find(t => t.id === id)
    };

    onChange(edited);
    onClose();
  };

  const handleEnter = menu => {
    const menuRect = menu.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();
    setIsBottomMenu(
      anchorRect.height + anchorRect.top + menuRect.height < height
    );
    setMenuEl(menu);

    //TODO:
    // material ui has a bug: its modals not lock scroll on iOS devices in some cases
    // so onOpen call below is a workaround that manually locks schedule scroll
    // but it dramatically decreases performance. need to investigate and fix.
    // feature is disabled in parent
    if (onOpen) {
      onOpen();
    }
  };

  const handleEntering = menu => {
    if (menu && anchorEl && arrowRef.current) {
      const menuRect = menu.getBoundingClientRect();
      const anchorRect = anchorEl.getBoundingClientRect();
      const arrowRect = arrowRef.current.getBoundingClientRect();

      setIsBottomMenu(
        anchorRect.height + anchorRect.top + menuRect.height < height
      );
      setArrowLeft(
        `${
          anchorRect.left +
          anchorRect.width / 2 -
          menuRect.left -
          arrowRect.width / 2
        }px`
      );
    }
  };

  const handleCancelConfirmClick = () => {
    const edited = {
      ...workoutData,
      facility: null
    };

    onChange(edited);
    onClose();
  };

  useEffect(() => {
    if (menuEl && anchorEl) {
      handleEntering(menuEl);
    }
  }, [menuRect.left, width, height]);

  return (
    <Menu
      closeAfterTransition
      TransitionComponent={Fade}
      id="customized-menu"
      className={clsx(classes.dateMenu, { [classes.menuTop]: !isBottomMenu })}
      anchorEl={anchorEl}
      open={isOpen}
      onClose={handleClose}
      TransitionProps={{
        onExited: handleExited,
        onEnter: handleEnter,
        onEntering: handleEntering
      }}
      elevation={24}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: isBottomMenu ? "bottom" : "top",
        horizontal: "center"
      }}
      transformOrigin={{
        vertical: isBottomMenu ? "top" : "bottom",
        horizontal: "center"
      }}
    >
      <div
        ref={arrowRef}
        className={clsx(classes.arrow, {
          [classes.arrowBottom]: !isBottomMenu
        })}
        style={arrowLeft ? { left: arrowLeft } : {}}
      />
      {isOpen && (
        <Box className={classes.slidesContainer}>
          <Box
            className={clsx(
              classes.menuSlide,
              activeView === "default"
                ? classes.mainMenuShow
                : classes.mainMenuHide
            )}
          >
            <MenuItem
              onClick={handleChangeFacilityClick}
              // Next two days validation temporarily discarded as per https://xbglobal.atlassian.net/browse/RAC-2657
              // className={clsx({
              //   [classes.disabled]: !isAfterTomorrow(
              //     workoutData ? parseISO(workoutData.date) : null
              //   )
              // })}
            >
              <ListItemIcon>
                <FacilityIcon
                  className={classes.facilityIconTransform}
                  selected={false}
                  code={workoutData?.facility?.code.trim()}
                  backgroundColor={workoutData?.facility?.backgroundColor}
                  strokeColor={workoutData?.facility?.strokeColor}
                />
              </ListItemIcon>
              <ListItemText primary="Change Track" />
              <ListItemIcon className={classes.arrowRight}>
                <ChevronRight fontSize="small" />
              </ListItemIcon>
            </MenuItem>
            <Divider light />
            <LinkMenuItem
              route={routes.workoutComments.path.replace(
                ":workoutId",
                String(workoutData?.id)
              )}
              Icon={CommentIcon}
              label="Add or Edit Comment"
            />
            <Divider light />
            <CancelConfirmation
              horse={horse}
              workoutData={workoutData}
              onConfirm={handleCancelConfirmClick}
            />
          </Box>

          <Box
            className={clsx(
              classes.menuSlide,
              activeView === "facility"
                ? classes.facilityMenuShow
                : classes.facilityMenuHide
            )}
          >
            <MenuItem className={classes.backButton} onClick={handleBackClick}>
              <ListItemIcon>
                <ArrowBack fontSize="small" className={classes.backIcon} />
              </ListItemIcon>
              <ListItemText primary="Back" />
            </MenuItem>
            <Divider light />
            <Box className={classes.facilitiesWrapper}>
              {Boolean(facilityOptions?.length) &&
                facilityOptions?.map(facility => (
                  <ScheduleDateMenuFacility
                    key={facility.id}
                    facility={facility}
                    workoutData={workoutData}
                    onClick={handleFacilityClick}
                  />
                ))}
            </Box>
          </Box>
        </Box>
      )}
    </Menu>
  );
};

export default React.memo(ScheduleDateMenu);
